import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Zap, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();


  const handleDemo = async () => {
    setLoading(true);
    try {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed-demo`, { method: 'POST' });
      } catch (e) {}
      const user = await login('demo@placedesenergies.com', 'DEMOPDE');
      toast.success('Bienvenue dans l\'espace démo !');
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error('Erreur de connexion démo');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Connexion réussie !');
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        {/* Background image with dark overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1583171902676-cb24cf6bcb74?auto=format&fit=crop&w=1600&q=80" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#191D1E]/85" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div>
            <img 
              src="/logo_white.svg" 
              alt="Place des Energies" 
              className="h-12 w-auto"
            />
          </div>

          {/* Main content */}
          <div className="max-w-lg">
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              MarketAlert
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-md">
              Soyez informé au bon moment pour renégocier vos contrats d'énergie professionnels.
            </p>

            {/* Feature badges */}
            <div className="mt-10 flex flex-col gap-4">
              <div className="flex items-center gap-4 bg-white/8 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#397DFF]/30 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-[#397DFF]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">100% Automatisé</p>
                  <p className="text-white/50 text-xs">Extraction des prix par IA depuis vos newsletters</p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/8 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#10D183]/30 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#10D183]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Surveillance 24/7</p>
                  <p className="text-white/50 text-xs">Les prix du marché sont suivis en continu</p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/8 backdrop-blur-sm rounded-xl px-5 py-4 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#F9C80E]/30 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#F9C80E]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Alertes en temps réel</p>
                  <p className="text-white/50 text-xs">Notification par e-mail dès qu'un seuil est atteint</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <p className="text-white/30 text-sm">Place des Energies &copy; {new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src="/logo_dark.svg" 
              alt="Place des Energies" 
              className="h-10 w-auto mb-3"
            />
            <p className="text-[#687175] text-sm">MarketAlert</p>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-[#BFC6CC]/12">
            <h2 className="text-2xl font-bold text-[#191D1E] mb-1">Connexion</h2>
            <p className="text-[#687175] text-sm mb-6">Accedez a votre espace personnel</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[#191D1E] font-medium text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC]/30 text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/15 rounded-xl"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#191D1E] font-medium text-sm">Mot de passe</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    className="bg-[#F5F6F9] border-[#BFC6CC]/30 text-[#191D1E] placeholder-[#939BA0] focus:border-[#397DFF] focus:ring-[#397DFF]/15 pr-10 rounded-xl"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#939BA0] hover:text-[#687175]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary h-12 text-base"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#BFC6CC]/30"></div></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-[#939BA0]">ou</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base border-[#397DFF]/30 text-[#397DFF] hover:bg-[#397DFF]/5"
              onClick={handleDemo}
              disabled={loading}
              data-testid="demo-btn"
            >
              Accès Démo
            </Button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
